import * as React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import cx from 'classnames';
import { ItemOptions } from './';

export enum FittingMode {
  fixed = 'fixed',
  scroll = 'scroll',
  static = 'static',
}

export enum ToggleMode {
  independent = 'independent',
  limitOne = 'limit-one',
  alwaysShowOne = 'always-show-one',
}

export interface Props extends ItemOptions {
  className?: string;
  fittingMode?: 'fixed' | 'scroll' | 'static';
  toggleMode?: ToggleMode;
}

interface State {
  openItem: number;
}

class Accordion extends React.Component<Props, State> {
  public static defaultProps: Props = {
    content: {},
    fittingMode: FittingMode.fixed,
    heading: {},
    toggleMode: ToggleMode.alwaysShowOne,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      openItem:
        React.Children.map(
          (props as any).children,
          (child: React.ReactElement<any>, index) =>
            child && child.props.open ? index + 1 : 0,
        ).reduce((a, v) => a || v, 0) - 1,
    };
  }

  componentWillReceiveProps(nextProps) {
    const countChanged =
      this.countSections(this.props) !== this.countSections(nextProps);
    const newCountIsOne = this.countSections(nextProps) === 1;
    const shouldEnsureFirstItemIsOpen =
      countChanged &&
      newCountIsOne &&
      nextProps.toggleMode === ToggleMode.alwaysShowOne;

    if (shouldEnsureFirstItemIsOpen) {
      this.setState({ openItem: 0 });
    }
  }

  public render() {
    const {
      className,
      children,
      content,
      fittingMode,
      toggleMode,
      heading,
    } = this.props;
    const { openItem } = this.state;

    const allowToggling = !(
      this.countSections(this.props) === 1 &&
      toggleMode === ToggleMode.alwaysShowOne
    );

    return (
      <ul className={cx(className, `fit-${fittingMode}`)}>
        {React.Children.map(
          children,
          (child: React.ReactElement<any>, index) => {
            // null children from conditional rendering can be ignored
            if (!child) {
              return;
            }
            const { content: c, heading: h, open: o } = child.props;
            // parent options (if given) override child options
            return React.cloneElement(child, {
              className: cx(child.props.className, 'item'),
              content: {
                ...c,
                ...content,
                toggleAccordionItem:
                  allowToggling && this.handleToggleClick.bind(null, index),
              },
              heading: { ...h, ...heading, allowToggling },
              onClick: this.handleToggleClick.bind(this, index),
              open:
                this.getToggleType() === ToggleMode.limitOne ||
                this.getToggleType() === ToggleMode.alwaysShowOne
                  ? openItem === index
                  : o,
            });
          },
        )}
      </ul>
    );
  }

  private countSections = (props): number =>
    React.Children.toArray(props.children).length;

  private getToggleType = ():
    | 'independent'
    | 'limit-one'
    | 'always-show-one' => {
    const { fittingMode, toggleMode } = this.props;
    return fittingMode === FittingMode.fixed
      ? ToggleMode.alwaysShowOne
      : toggleMode;
  };

  private handleToggleClick = (index: number, e) => {
    if (this.getToggleType() === ToggleMode.limitOne) {
      this.setState(prevState => ({
        openItem: prevState.openItem === index ? -1 : index,
      }));
    } else if (this.getToggleType() === ToggleMode.alwaysShowOne) {
      this.setState(prevState => {
        const closingFirstDrawer = prevState.openItem === 0 && index === 0;
        const closingAnotherDrawer =
          prevState.openItem !== 0 && index === prevState.openItem;

        if (closingFirstDrawer) {
          const hasMoreThanOneSection = this.countSections(this.props) > 1;
          return { openItem: hasMoreThanOneSection ? 1 : 0 };
        }
        if (closingAnotherDrawer) {
          return { openItem: 0 };
        }
        return { openItem: index };
      });
    }
  };
}

export default styled(Accordion)`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  padding: 0;

  .item.open + .item .heading .shadow {
    display: block;

    &:before {
      visibility: visible;
    }
  }

  /*
  fittingMode: static
  requires:
    - parent container with height auto
  useful for:
    - toggleMode: independent
    - top level page sectioning
    - mobile views
  */
  &.fit-static {
  }

  /*
  fittingMode: scroll
  requires: parent container with a set height
  useful for:
    - toggleMode: independent
    - multiple items can be open at once
    - numerous headings take up more space than available
  */
  &.fit-scroll {
    overflow-x: hidden;
    overflow-y: auto;
  }

  /*
  fittingMode: fixed
  requires:
    - parent container with a set height
    - toggleMode: limit-one
  useful for:
    - few headings and scrollable contents
  */
  &.fit-fixed {
    .item.open {
      flex-grow: 1;
      flex-shrink: 1;

      .content {
        flex-grow: 1;
        flex-shrink: 1;
        overflow-x: visible;
        overflow-y: auto;
      }
    }
  }
`;
